import React, { useState, useEffect } from 'react';
import { Button } from './UI';

/**
 * GateOpenButton — кнопка «Открыть ворота» на въезде.
 * Показывается только в окне заезда (session.status === 'assigned' и назначен бокс).
 * Ровно одно нажатие на сессию (backend отдаёт 409 на повтор).
 * Если управление воротами отключено киллсвитчем — показывает
 * «Управление воротами не доступно».
 *
 * @param {Object} session - сессия (status, box_id, gate_opened_at)
 * @param {Function} openGate - () => Promise<{success, message}> (уже привязана к сессии/токену)
 * @param {Function} getGateStatus - () => Promise<{disabled: boolean}>
 * @param {string} theme - 'light' | 'dark'
 */
const GateOpenButton = ({ session, openGate, getGateStatus, theme = 'light' }) => {
  const [disabled, setDisabled] = useState(null); // null = статус ещё не загружен
  const [opened, setOpened] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isAssigned = session?.status === 'assigned' && !!session?.box_id;

  useEffect(() => {
    if (!isAssigned) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const st = await getGateStatus();
        if (!cancelled) setDisabled(!!st?.disabled);
      } catch (e) {
        // Не смогли узнать статус — считаем недоступным (пилот выключен по умолчанию)
        if (!cancelled) setDisabled(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssigned]);

  if (!isAssigned || disabled === null) return null;

  const dark = theme === 'dark';

  const infoStyle = {
    marginTop: '8px',
    padding: '10px 12px',
    borderRadius: '8px',
    fontSize: '14px',
    textAlign: 'center',
  };

  if (disabled) {
    return (
      <div
        style={{
          ...infoStyle,
          color: dark ? '#bbb' : '#777',
          backgroundColor: dark ? 'rgba(255,255,255,0.06)' : '#f2f2f2',
        }}
      >
        🚧 Управление воротами не доступно
      </div>
    );
  }

  const alreadyOpened = opened || !!session.gate_opened_at;

  if (alreadyOpened) {
    return (
      <div
        style={{
          ...infoStyle,
          color: dark ? '#81c784' : '#2e7d32',
          backgroundColor: dark ? 'rgba(46,125,50,0.15)' : '#e8f5e9',
        }}
      >
        ✅ {message || 'Ворота открываются'}
      </div>
    );
  }

  const handleOpen = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await openGate();
      setOpened(true);
      setMessage(resp?.message || 'Ворота открываются');
    } catch (e) {
      const status = e?.response?.status;
      const serverMsg = e?.response?.data?.error;
      if (status === 409) {
        // Уже открывали в этой сессии
        setOpened(true);
        setMessage('Ворота уже открывались');
      } else if (status === 403) {
        setDisabled(true);
      } else {
        setError(serverMsg || 'Не удалось открыть ворота. Обратитесь к кассиру.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: '8px' }}>
      <Button
        theme={theme}
        onClick={handleOpen}
        disabled={loading}
        loading={loading}
        style={{ width: '100%', backgroundColor: '#455a64' }}
      >
        🚪 Открыть ворота
      </Button>
      {error && (
        <div style={{ ...infoStyle, color: '#c62828', backgroundColor: dark ? 'rgba(198,40,40,0.15)' : '#ffebee' }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default GateOpenButton;
